import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Collapsible, CollapsibleContent } from '../../components/ui/collapsible';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  TrendingUp, Package, Clock, CheckCircle2, AlertTriangle,
  FileText, Truck, PackageCheck, Plus, ChevronDown, ChevronUp,
  BarChart3, PieChart as PieChartIcon, Layers
} from 'lucide-react';
import { formatNumber } from '../../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import DashboardFilters from './DashboardFilters';
import DrillDownModal from './DrillDownModal';
import { useCompany } from '../../contexts/CompanyContext';

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg shadow-sm bg-white mb-6">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors rounded-t-lg">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-slate-600" />}
          <span className="font-semibold text-slate-900">{title}</span>
          {badge != null && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{badge}</span>}
        </div>
        <div className="text-slate-400">{isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}</div>
      </button>
      <Collapsible open={isOpen}>
        <CollapsibleContent>
          <div className="p-4 pt-0 border-t border-slate-100">{children}</div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

const CHART_COLORS = ['#0F172A', '#0D9488', '#F59E0B', '#EF4444', '#64748B', '#8B5CF6', '#EC4899', '#06B6D4'];

const statusColors = {
  'Pending': 'bg-red-50 text-red-700 border-red-200',
  'Partially Billed': 'bg-amber-50 text-amber-700 border-amber-200',
  'Completed': 'bg-teal-50 text-teal-700 border-teal-200'
};

const OverviewDashboard = ({ filters, setFilters, filterOptions }) => {
  const navigate = useNavigate();
  const { activeCompany } = useCompany();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [drilldown, setDrilldown] = useState({ open: false, data: null, loading: false });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/dashboard/overview-data?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData, activeCompany?.company_id]);

  const openDrilldown = async (metric) => {
    setDrilldown({ open: true, data: null, loading: true });
    try {
      const params = new URLSearchParams({ metric });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/dashboard/drilldown?${params.toString()}`);
      setDrilldown({ open: true, data: res.data, loading: false });
    } catch {
      toast.error('Failed to load drill-down data');
      setDrilldown({ open: false, data: null, loading: false });
    }
  };

  if (loading && !data) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-slate-200 rounded"></div>)}
        </div>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const supplierAnalytics = data?.supplier_analytics || [];
  const qualityAnalytics = data?.quality_analytics || [];
  const itemAnalytics = data?.item_analytics || [];
  const alerts = data?.operational_alerts || {};
  const recentOrders = data?.recent_orders || [];

  return (
    <>
      <DashboardFilters filters={filters} setFilters={setFilters} filterOptions={filterOptions} showFinishQuality showLRStatus />

      {/* KPI Section */}
      <CollapsibleSection title="Order Overview" icon={Package} badge={kpis.total_orders}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="kpi-total-orders" className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDrilldown('total_orders')}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Orders</CardTitle>
              <Package className="h-5 w-5 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{kpis.total_orders || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-pending-orders" className="border border-red-200 shadow-sm hover:shadow-md transition-shadow bg-red-50/50 cursor-pointer" onClick={() => openDrilldown('pending_orders')}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-red-700">Pending Orders</CardTitle>
              <Clock className="h-5 w-5 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-700" style={{ fontFamily: 'Manrope' }}>{kpis.pending_orders || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-partial-orders" className="border border-amber-200 shadow-sm hover:shadow-md transition-shadow bg-amber-50/50 cursor-pointer" onClick={() => openDrilldown('partially_billed')}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-amber-700">Partially Billed</CardTitle>
              <TrendingUp className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-700" style={{ fontFamily: 'Manrope' }}>{kpis.partially_billed_orders || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="kpi-completed-orders" className="shadow-sm hover:shadow-md transition-shadow cursor-pointer" style={{ border: '1px solid var(--c-200)', backgroundColor: 'color-mix(in srgb, var(--c-50) 50%, transparent)' }} onClick={() => openDrilldown('completed_orders')}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium theme-text-dark">Completed Orders</CardTitle>
              <CheckCircle2 className="h-5 w-5" style={{ color: 'var(--c-500)' }} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold theme-text-dark" style={{ fontFamily: 'Manrope' }}>{kpis.completed_orders || 0}</div>
            </CardContent>
          </Card>
        </div>
      </CollapsibleSection>

      {/* LR Status */}
      {kpis.lr_status && (
        <CollapsibleSection title="LR Status Overview" icon={Truck}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card data-testid="kpi-lr-in-transport" className="border border-blue-200 shadow-sm hover:shadow-md transition-shadow bg-blue-50/50 cursor-pointer" onClick={() => openDrilldown('lr_in_transport')}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-blue-700">LRs In Transport</CardTitle>
                <Truck className="h-5 w-5 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-700" style={{ fontFamily: 'Manrope' }}>{kpis.lr_status.in_transport || 0}</div>
                <p data-testid="lr-in-transport-qty" className="text-xs text-blue-600 mt-1">
                  {formatNumber(kpis.lr_quantities?.in_transport?.bales || 0)} Bales
                </p>
              </CardContent>
            </Card>
            <Card data-testid="kpi-lr-issued" className="border border-amber-200 shadow-sm hover:shadow-md transition-shadow bg-amber-50/50 cursor-pointer" onClick={() => openDrilldown('lr_issued')}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-amber-700">LRs Issued for Delivery</CardTitle>
                <FileText className="h-5 w-5 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-700" style={{ fontFamily: 'Manrope' }}>{kpis.lr_status.issued_for_delivery || 0}</div>
                <p className="text-xs text-amber-600 mt-1">
                  {formatNumber(kpis.lr_quantities?.issued_for_delivery?.bales || 0)} Bales
                </p>
              </CardContent>
            </Card>
            <Card data-testid="kpi-lr-delivered" className="border border-green-200 shadow-sm hover:shadow-md transition-shadow bg-green-50/50 cursor-pointer" onClick={() => openDrilldown('lr_delivered')}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-green-700">LRs Delivered</CardTitle>
                <PackageCheck className="h-5 w-5 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-700" style={{ fontFamily: 'Manrope' }}>{kpis.lr_status.delivered || 0}</div>
                <p className="text-xs text-green-600 mt-1">
                  {formatNumber(kpis.lr_quantities?.delivered?.bales || 0)} Bales
                </p>
              </CardContent>
            </Card>
          </div>
        </CollapsibleSection>
      )}

      {/* Quantity Summary */}
      {kpis.unit_wise_quantities && Object.keys(kpis.unit_wise_quantities).length > 0 && (
        <CollapsibleSection title="Quantity Summary by Unit" icon={Layers}>
          <div className="space-y-4">
            {Object.entries(kpis.unit_wise_quantities).map(([unit, quantities]) => (
              <div key={unit} className="border border-slate-100 rounded-lg p-4 bg-slate-50/50">
                <h3 className="text-base font-medium text-slate-700 mb-3 uppercase tracking-wide">{unit}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Ordered</p>
                    <p className="text-lg font-bold text-slate-900">{formatNumber(quantities.ordered)}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Billed</p>
                    <p className="text-lg font-bold text-slate-900">{formatNumber(quantities.billed)}</p>
                  </div>
                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--c-50)', border: '1px solid var(--c-200)' }}>
                    <p className="text-xs mb-1 theme-text">Received</p>
                    <p className="text-lg font-bold theme-text-dark">{formatNumber(quantities.received)}</p>
                  </div>
                  <div className="bg-white p-3 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-500 mb-1">Pending Quantity</p>
                    <p className="text-lg font-bold text-slate-900">{formatNumber(quantities.pending)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Item-wise Analytics */}
      {itemAnalytics.length > 0 && (
        <CollapsibleSection title="Item-wise Analytics" icon={BarChart3} badge={itemAnalytics.length}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Item</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Orders</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Ordered</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Billed</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Received</th>
                  <th className="text-right py-3 px-4 font-semibold text-slate-700">Pending Quantity</th>
                </tr>
              </thead>
              <tbody>
                {itemAnalytics.map((item, index) => (
                  <tr key={item.item_name || index} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-900">{item.item_name || '-'}</td>
                    <td className="py-3 px-4 text-right text-slate-600">{item.order_count}</td>
                    <td className="py-3 px-4 text-right text-slate-900 font-medium">{formatNumber(item.total_ordered)}</td>
                    <td className="py-3 px-4 text-right text-slate-600">{formatNumber(item.total_billed)}</td>
                    <td className="py-3 px-4 text-right font-medium theme-text-dark">{formatNumber(item.total_received)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={item.pending > 0 ? 'text-amber-600 font-medium' : 'text-slate-600'}>
                        {formatNumber(item.pending)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      )}

      {/* Charts */}
      <CollapsibleSection title="Analytics" icon={PieChartIcon}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base font-semibold text-slate-900">Top Suppliers</CardTitle></CardHeader>
            <CardContent>
              {supplierAnalytics.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={supplierAnalytics} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" stroke="#64748b" style={{ fontSize: '11px' }} />
                    <YAxis dataKey="supplier_name" type="category" width={100} stroke="#64748b" style={{ fontSize: '11px' }} />
                    <Tooltip />
                    <Bar dataKey="total_ordered" fill="#0D9488" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">No data available</div>
              )}
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-base font-semibold text-slate-900">Quality Distribution</CardTitle></CardHeader>
            <CardContent>
              {qualityAnalytics.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={qualityAnalytics} dataKey="total_ordered" nameKey="quality_name" cx="50%" cy="50%" outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={false}>
                      {qualityAnalytics.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">No data available</div>
              )}
            </CardContent>
          </Card>
        </div>
      </CollapsibleSection>

      {/* Operational Alerts */}
      {(alerts.orders_with_bills_no_lr?.length > 0 || alerts.orders_with_lr_no_receipts?.length > 0 || alerts.long_pending_orders?.length > 0) && (
        <CollapsibleSection title="Operational Alerts" icon={AlertTriangle}>
          <div className="space-y-4">
            {alerts.orders_with_bills_no_lr?.length > 0 && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-900 mb-2">Orders with Bills but No LR ({alerts.orders_with_bills_no_lr.length})</p>
                <div className="flex flex-wrap gap-2">
                  {alerts.orders_with_bills_no_lr.slice(0, 8).map((orderNum) => (
                    <Badge key={orderNum} variant="outline" className="bg-white cursor-pointer hover:bg-amber-100">{orderNum}</Badge>
                  ))}
                  {alerts.orders_with_bills_no_lr.length > 8 && <Badge variant="secondary">+{alerts.orders_with_bills_no_lr.length - 8} more</Badge>}
                </div>
              </div>
            )}
            {alerts.orders_with_lr_no_receipts?.length > 0 && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-900 mb-2">Orders with LR but No Receipts ({alerts.orders_with_lr_no_receipts.length})</p>
                <div className="flex flex-wrap gap-2">
                  {alerts.orders_with_lr_no_receipts.slice(0, 8).map((orderNum) => (
                    <Badge key={orderNum} variant="outline" className="bg-white cursor-pointer hover:bg-amber-100">{orderNum}</Badge>
                  ))}
                  {alerts.orders_with_lr_no_receipts.length > 8 && <Badge variant="secondary">+{alerts.orders_with_lr_no_receipts.length - 8} more</Badge>}
                </div>
              </div>
            )}
            {alerts.long_pending_orders?.length > 0 && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-900 mb-2">Long Pending Orders (&gt;30 days) ({alerts.long_pending_orders.length})</p>
                <div className="flex flex-wrap gap-2">
                  {alerts.long_pending_orders.slice(0, 8).map((orderNum) => (
                    <Badge key={orderNum} variant="outline" className="bg-white cursor-pointer hover:bg-red-100 border-red-300 text-red-700">{orderNum}</Badge>
                  ))}
                  {alerts.long_pending_orders.length > 8 && <Badge variant="secondary">+{alerts.long_pending_orders.length - 8} more</Badge>}
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Recent Orders */}
      <CollapsibleSection title="Recent Orders" icon={FileText} badge={recentOrders.length}>
        <div className="flex justify-end mb-4">
          <Button data-testid="create-order-btn" onClick={() => navigate('/grey-orders/create')} size="sm" className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" /> New Order
          </Button>
        </div>
        {recentOrders.length > 0 ? (
          <div className="space-y-3">
            {recentOrders.map((order) => (
              <div key={order.order_id} data-testid={`order-item-${order.order_number}`}
                className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/grey-orders/${order.order_id}`)}>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono font-medium text-slate-900">{order.order_number}</span>
                    <Badge className={statusColors[order.order_status] || ''}>{order.order_status}</Badge>
                  </div>
                  <p className="text-sm text-slate-600">
                    {order.supplier_name} {order.quality_name && `\u00B7 ${order.quality_name}`}
                    {(order.item || order.item_name) && ` \u00B7 ${order.item || order.item_name}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900">{formatNumber(order.ordered_quantity)} {order.order_unit}</p>
                  <p className="text-xs text-slate-500">{order.order_date}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No orders found</p>
          </div>
        )}
      </CollapsibleSection>

      <DrillDownModal
        open={drilldown.open}
        onClose={() => setDrilldown({ open: false, data: null, loading: false })}
        data={drilldown.data}
        loading={drilldown.loading}
      />
    </>
  );
};

export default OverviewDashboard;
